import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ConversationItem } from "@/features/conversations/components/ConversationItem";
import { ChevronDown, Pin } from "lucide-react";
import { format } from "date-fns";
import { tr, enUS, de, ar, es, fr, it, type Locale } from "date-fns/locale";
import useModalStore from "@/store/use-modal-store";
import { cn } from "@/lib/utils";
import { useLeftSidebar } from "@/components/ui/sidebar";
import { useSidebarConversations } from "@/features/conversations/hooks/use-sidebar-conversations";
import useSidebarStore from "@/store/use-sidebar-store";
import usePinnedChatsStore from "@/store/use-pinned-chats-store";
import { VirtualList } from "@/components/ui/virtual-list";
import { Conversation } from "@/api/models/api-types";
import { useActiveModule } from "@/modules";
import { useChatDockStore } from "@/features/chat-dock/store/chat-dock-store";

const DATE_LOCALES: Record<string, Locale> = { tr, en: enUS, de, ar, es, fr, it };

type ConversationRow =
  | {
      type: "header";
      key: string;
      groupKey: string;
      label: string;
      count: number;
      collapsed: boolean;
      isPinnedGroup?: boolean;
    }
  | { type: "item"; key: string; conversation: Conversation };

interface ConversationGroup {
  key: string;
  label: string;
  isPinnedGroup?: boolean;
  items: Conversation[];
}

/**
 * Collapsible tarih/pin grup başlığı.
 * Chevron: grup kapalıyken her zaman görünür (-90°), açıkken hover'da belirir.
 */
const GroupHeaderRow: React.FC<{
  label: string;
  count: number;
  collapsed: boolean;
  isPinnedGroup?: boolean;
  onToggle: () => void;
}> = ({ label, count, collapsed, isPinnedGroup, onToggle }) => (
  <button
    onClick={onToggle}
    className="group/header w-full flex items-center gap-1.5 px-2 pt-3 pb-1 select-none rounded-md focus:outline-none"
    aria-expanded={!collapsed}
  >
    {isPinnedGroup && (
      <Pin className="h-3 w-3 shrink-0 fill-current text-module-accent/80 dark:text-module-accent/70" />
    )}
    <span className="text-xs font-medium truncate text-text-quaternary dark:text-gray-500 group-hover/header:text-text-tertiary dark:group-hover/header:text-gray-400 transition-colors">
      {label}
    </span>
    <span className="text-[10px] font-medium shrink-0 text-text-quaternary/60 dark:text-gray-600">
      {count}
    </span>
    <ChevronDown
      className={cn(
        "ml-auto h-3.5 w-3.5 shrink-0 text-text-quaternary dark:text-gray-500 transition-all duration-200",
        collapsed
          ? "-rotate-90 opacity-100"
          : "opacity-0 group-hover/header:opacity-100"
      )}
    />
  </button>
);

export default function Conversations() {
  const { t, i18n } = useTranslation();
  const { open: isSidebarOpen } = useLeftSidebar();
  const {
    conversations,
    onChatSelect,
    onChatDelete,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isModuleScoped,
  } = useSidebarConversations();
  // Modül içinde aktif konuşma dock'ta açık olandır (URL değişmez)
  const activeModule = useActiveModule();
  const dockChatId = useChatDockStore((s) =>
    activeModule ? s.chatByModule[activeModule.id] : undefined
  );
  // Dock kapalıyken konuşma "aktif" değildir → okunmadı noktası görünür
  const isDockOpen = useChatDockStore((s) => s.isOpen);
  const { openDeleteChat } = useModalStore();
  const { collapsedGroups, toggleGroup } = useSidebarStore();
  const { pinnedIds, isPinned, togglePin, unpin } = usePinnedChatsStore();

  // ✅ Pin (client-side) + tarihe göre gruplama; grup açık/kapalı durumu
  // useSidebarStore.collapsedGroups içinde persist ediliyor (stable key'lerle).
  const rows = useMemo<ConversationRow[]>(() => {
    if (conversations.length === 0) return [];

    const dateLocale =
      DATE_LOCALES[i18n.language?.split("-")[0] ?? "en"] ?? enUS;

    const pinnedSet = new Set(pinnedIds);
    const pinnedConversations = pinnedIds
      .map((id) => conversations.find((c) => c._id === id))
      .filter((c): c is Conversation => !!c);
    const restConversations = conversations.filter(
      (c) => !pinnedSet.has(c._id)
    );

    const groups: ConversationGroup[] = [];

    if (pinnedConversations.length > 0) {
      groups.push({
        key: "pinned",
        label: t("chat.pinned"),
        isPinnedGroup: true,
        items: pinnedConversations,
      });
    }

    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const last7Days = new Date(now);
    last7Days.setDate(last7Days.getDate() - 7);
    const last30Days = new Date(now);
    last30Days.setDate(last30Days.getDate() - 30);

    restConversations.forEach((conversation) => {
      const date = new Date(conversation.createdAt);
      let key: string;
      let label: string;

      if (date.toDateString() === now.toDateString()) {
        key = "today";
        label = t("chat.today");
      } else if (date.toDateString() === yesterday.toDateString()) {
        key = "yesterday";
        label = t("chat.yesterday");
      } else if (date > last7Days) {
        key = "prev7";
        label = t("chat.previous7Days");
      } else if (date > last30Days) {
        key = "prev30";
        label = t("chat.previous30Days");
      } else {
        key = `m-${format(date, "yyyy-MM")}`;
        label = format(date, "MMMM yyyy", { locale: dateLocale });
      }

      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.key === key) {
        lastGroup.items.push(conversation);
      } else {
        groups.push({ key, label, items: [conversation] });
      }
    });

    const result: ConversationRow[] = [];
    for (const group of groups) {
      const collapsed = !!collapsedGroups[group.key];
      result.push({
        type: "header",
        key: `header-${group.key}`,
        groupKey: group.key,
        label: group.label,
        count: group.items.length,
        collapsed,
        isPinnedGroup: group.isPinnedGroup,
      });
      if (!collapsed) {
        group.items.forEach((c) =>
          result.push({ type: "item", key: c._id, conversation: c })
        );
      }
    }

    return result;
  }, [conversations, pinnedIds, collapsedGroups, t, i18n.language]);

  // 🆕 Rail (daraltılmış) modda sohbet listesi gizli — modern rail sadece
  // ikon aksiyonları taşır; liste sidebar açılınca görünür.
  if (!isSidebarOpen) {
    return null;
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Bölüm etiketi */}
      <div className="shrink-0 px-4 pt-2.5 pb-0.5">
        <span className="text-[11px] font-bold uppercase tracking-widest text-text-quaternary dark:text-gray-600">
          {t("common.chats")}
        </span>
      </div>

      {/* ✅ VirtualList: infinite scroll built-in, tek kaydırılabilir alan */}
      <VirtualList
        data={rows}
        renderItem={(row) =>
          row.type === "header" ? (
            <GroupHeaderRow
              label={row.label}
              count={row.count}
              collapsed={row.collapsed}
              isPinnedGroup={row.isPinnedGroup}
              onToggle={() => toggleGroup(row.groupKey)}
            />
          ) : (
            <ConversationItem
              key={row.conversation._id}
              conversation={row.conversation}
              isActive={
                isModuleScoped
                  ? isDockOpen && dockChatId === row.conversation._id
                  : window.location.pathname ===
                    `/chat/${row.conversation._id}`
              }
              isSidebarOpen={isSidebarOpen}
              isPinned={isPinned(row.conversation._id)}
              onSelect={(id) => onChatSelect(id)}
              onDelete={(id) => {
                openDeleteChat(() => {
                  unpin(id);
                  onChatDelete(id);
                });
              }}
              onTogglePin={togglePin}
            />
          )
        }
        keyExtractor={(row) => row.key}
        estimatedItemSize={44}
        onEndReached={fetchNextPage}
        onEndReachedThreshold={0.85}
        infiniteQuery={{
          hasNextPage,
          isFetchingNextPage,
          isLoading,
        }}
        className="flex-1 h-full px-2 pb-2"
        style={{
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
        }}
        ListEmptyComponent={
          <div className="text-sm text-[#717680] dark:text-gray-400 text-center py-4">
            {t("chat.noConversations")}
          </div>
        }
        LoadingMoreComponent={
          <div className="flex justify-center items-center py-4">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
          </div>
        }
        EndOfListComponent={<div className="h-1" aria-hidden="true" />}
        overscan={3}
      />
    </div>
  );
}
